import IconChevronRight from 'assets/icon-chevron-right.svg';
import IconChevronLeft from 'assets/icon-chevron-left.svg';
import useLang from 'lang';
import { formatLocaleDate } from 'lang/date-time';
import { useMemo } from 'react';

/** Returns true if the given date is before today. */
function isPast(year: number, month: number, day: number) {
  const d = new Date(year, month, day);
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return d < t;
}

/** Returns true if the weekday of the given date is in the space's available days list. */
function isDayAvailable(year: number, month: number, day: number, availableDays: number[]) {
  const jsDow = new Date(year, month, day).getDay();
  const apiDow = jsDow === 0 ? 7 : jsDow;
  return availableDays.includes(apiDow);
}

/** Filters the slot list to only those that start on the given calendar day. */
function getSlotsForDay(allSlots: any[], year: number, month: number, day: number) {
  return allSlots.filter((s) => {
    const d = new Date(s.startsAt);
    return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
  });
}

/** Returns true if every slot on the given day is unavailable. */
function isDayFullyOccupied(allSlots: any[], year: number, month: number, day: number) {
  const daySlots = getSlotsForDay(allSlots, year, month, day);
  return daySlots.length > 0 && daySlots.every((s) => !s.isAvailable);
}

/** Formats year/month/day into a YYYY-MM-DD string. */
function toDateStr(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export interface MonthViewProps {
  calYear: number;
  calMonth: number;
  calDays: { day: number; current: boolean }[];
  selectedDay: number | null;
  today: Date;
  availableDays: number[];
  slots: any[];
  loading: boolean;
  bookedDates: string[];
  onPrev: () => void;
  onNext: () => void;
  onSelect: (year: number, month: number, day: number) => void;
}

/**
 * Calendar grid showing one month with day availability indicators.
 */
export default function MonthView({
  calYear,
  calMonth,
  calDays,
  selectedDay,
  today,
  availableDays,
  slots,
  loading,
  bookedDates,
  onPrev,
  onNext,
  onSelect,
}: MonthViewProps) {
  const { _t, lang } = useLang();

  const DAYS_SHORT = useMemo(
    () =>
      Array.from({ length: 7 }, (_, dayOffset) =>
        formatLocaleDate(new Date(2024, 0, 1 + dayOffset), lang, { weekday: 'short' })
      ),
    [lang]
  );
  const monthLabel = useMemo(
    () => formatLocaleDate(new Date(calYear, calMonth, 1), lang, { month: 'long', year: 'numeric' }),
    [calMonth, calYear, lang]
  );

  return (
    <div className="calendar">
      <div className="calendar-header">
        <span className="calendar-month">{monthLabel}</span>
        <div className="calendar-nav">
          <button className="cal-nav-btn" onClick={onPrev}>
            <img src={IconChevronLeft} alt={_t('Previous month')} />
          </button>
          <button className="cal-nav-btn cal-nav-btn--next" onClick={onNext}>
            <img src={IconChevronRight} alt={_t('Next month')} />
          </button>
        </div>
      </div>
      <div className="calendar-grid">
        {DAYS_SHORT.map((d) => (
          <span key={d} className="cal-day-label">
            {d}
          </span>
        ))}
        {calDays.map((d, i) => {
          if (!d.current)
            return (
              <button key={i} className="cal-day cal-day--other" disabled>
                {d.day}
              </button>
            );

          const past = isPast(calYear, calMonth, d.day);
          const available = isDayAvailable(calYear, calMonth, d.day, availableDays);
          const full = !loading && isDayFullyOccupied(slots, calYear, calMonth, d.day);
          const alreadyBooked = bookedDates.includes(toDateStr(calYear, calMonth, d.day));
          const isToday = d.day === today.getDate() && calMonth === today.getMonth() && calYear === today.getFullYear();
          const isSelected = d.day === selectedDay;

          let cls = 'cal-day';
          if (isSelected) cls += ' cal-day--selected';
          else if (isToday) cls += ' cal-day--today';
          if (past || !available || alreadyBooked) cls += ' cal-day--unavailable';
          else if (full) cls += ' cal-day--full';

          return (
            <button
              key={i}
              className={cls}
              disabled={past || !available || alreadyBooked}
              onClick={() => onSelect(calYear, calMonth, d.day)}
            >
              {d.day}
              {full && !past && available && <span className="cal-day-dot" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
